import { useEffect } from 'react'
import { useTicketsStore } from '@/stores/tickets'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Activity
} from 'lucide-react'
import type { Database } from '@/types/database'

type Ticket = Database['public']['Tables']['tickets']['Row']

export function TicketSummary() {
  const { tickets, fetchTickets } = useTicketsStore()

  useEffect(() => {
    fetchTickets('emaildrafter')
  }, [fetchTickets])

  // Get recent activity (last 7 days)
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const inProgressTickets = tickets
    .filter(ticket => ticket.status === 'in_progress')
    .slice(0, 3)

  const recentlySolved = tickets
    .filter(ticket =>
      ticket.status === 'solved' || ticket.status === 'confirmed'
    )
    .filter(ticket => new Date(ticket.updated_at) > sevenDaysAgo)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 3)

  const highPriorityOpen = tickets
    .filter(ticket =>
      (ticket.status === 'open' || ticket.status === 'in_progress') &&
      (ticket.priority === 'high' || ticket.priority === 'urgent')
    )
    .slice(0, 3)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) return 'vor 1 Tag'
    if (diffDays < 7) return `vor ${diffDays} Tagen`
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'high': return 'bg-orange-500'
      case 'urgent': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_progress': return 'text-amber-600 dark:text-amber-400'
      case 'solved': return 'text-green-600 dark:text-green-400'
      case 'confirmed': return 'text-emerald-600 dark:text-emerald-400'
      default: return 'text-muted-foreground'
    }
  }

  const TicketItem = ({ ticket, showDate = false }: { ticket: Ticket, showDate?: boolean }) => (
    <div className="p-3 rounded-md border border-border hover:bg-muted/30 dark:hover:bg-muted/50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm leading-tight line-clamp-2 mb-1">
            {ticket.name}
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {ticket.created_by}
            </span>
            {showDate && (
              <span className="text-xs text-muted-foreground">
                • {formatDate(ticket.updated_at)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Badge
            variant="secondary"
            className={`text-white text-xs px-1.5 py-0.5 ${getPriorityColor(ticket.priority)}`}
          >
            {ticket.priority}
          </Badge>
          {ticket.status === 'in_progress' && (
            <Activity className={`h-3 w-3 ${getStatusColor(ticket.status)}`} />
          )}
          {(ticket.status === 'solved' || ticket.status === 'confirmed') && (
            <CheckCircle2 className={`h-3 w-3 ${getStatusColor(ticket.status)}`} />
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Currently In Progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-foreground flex items-center gap-2">
            <Activity className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            In Bearbeitung
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inProgressTickets.length > 0 ? (
            <div className="space-y-2">
              {inProgressTickets.map((ticket) => (
                <TicketItem key={ticket.id} ticket={ticket} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aktuell keine Tickets in Bearbeitung
            </p>
          )}
        </CardContent>
      </Card>

      {/* High Priority Open */}
      {highPriorityOpen.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              Hohe Priorität
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {highPriorityOpen.map((ticket) => (
                <TicketItem key={ticket.id} ticket={ticket} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recently Completed */}
      {recentlySolved.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              Kürzlich gelöst
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentlySolved.map((ticket) => (
                <TicketItem key={ticket.id} ticket={ticket} showDate />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Separate Statistics Component for placement after form
export function TicketStats() {
  const { tickets } = useTicketsStore()

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium text-foreground flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Statistiken
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Gesamt</span>
            <Badge variant="outline">{tickets.length}</Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Offen</span>
            <Badge variant="outline">
              {tickets.filter(t => t.status === 'open').length}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Gelöst</span>
            <Badge variant="outline" className="text-green-600 border-green-600">
              {tickets.filter(t => t.status === 'solved' || t.status === 'confirmed').length}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}